/**
 * `TestSuiteView` — the admin's "Test Suite" / CI board.
 *
 * Backs `GET /internal/admin/qa/ci-runs`. Mirrors the System Health
 * board's shape (mono kicker + serif title + caption + run-controls
 * row + body) so the two QA-console destinations feel like siblings.
 *
 * Three layouts:
 *   1. `configured:false` — calm empty state: "Connect GitHub to view
 *      CI runs" with a single-line note that a read-only token must
 *      be set (founder/ops). NO scary error styling — the dormant
 *      branch is the common case at first.
 *   2. `configured:true, runs:[]` — quiet "No runs to show" card;
 *      sometimes shipped alongside `error` (GitHub fetch failed), in
 *      which case the error class is surfaced inline.
 *   3. `configured:true, runs:[...]` — list of Cards, one per run,
 *      failures first, then cancelled, then in-progress, then success.
 *
 * Each row carries:
 *   - conclusion pill (mint=success / coral=failure / amber=cancelled
 *     / sky=in-progress / neutral=queued)
 *   - workflow name + display title (the staff's "what ran on what")
 *   - branch + short SHA in mono
 *   - first line of the commit message (single-line; server-truncated)
 *   - relative + absolute ET timestamps
 *   - link to the GitHub run page (always under
 *     `https://github.com/siteforbiz/sill/actions/runs/...`)
 *   - small "Escalate" button on FAILING runs that opens the shared
 *     EscalateDialog (kind=`'ci_run'`).
 *
 * Auto-loads on mount; subsequent refreshes are user-initiated via
 * the "Refresh" button (disabled + "Refreshing…" while in flight).
 *
 * Mobile: cards stack, taps clear 40px, the run-controls row stacks
 * with full-width buttons. The escalate dialog is itself responsive.
 */

import { ChevronDown, ExternalLink, FlaskConical, RefreshCw } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';

import type { AdminCiRun, AdminCiRunsResponse } from '@sill/types';

import { useAdminApi } from '../../api/api-context.js';
import { ApiError } from '../../api/http-client.js';
import { Card } from '../../components/atoms/Card.js';
import { EscalateDialog } from '../../components/atoms/EscalateDialog.js';
import type { EscalateContext } from '../../components/atoms/EscalateDialog.js';
import { ExportMenu } from '../../components/atoms/ExportMenu.js';
import { Pill } from '../../components/atoms/Pill.js';
import type { PillTone } from '../../components/atoms/Pill.js';
import { formatIsoCompact, formatRelativeShort } from '../../lib/format.js';
import {
  downloadCiJson,
  downloadCiMarkdown,
  rankCiRuns,
} from '../../lib/report-export.js';
import { useC } from '../../lib/theme.js';

const monoFont = "'JetBrains Mono', monospace";
const serifFont = "'Instrument Serif', serif";
const sansFont = "'Geist', sans-serif";

type ViewState =
  | { kind: 'loading' }
  | { kind: 'error'; message: string }
  | { kind: 'ready'; payload: AdminCiRunsResponse };

function errorMessage(err: unknown): string {
  if (err instanceof ApiError) {
    if (err.server_reason !== undefined && err.server_reason.length > 0) {
      return err.server_reason;
    }
    return err.kind;
  }
  return 'Unexpected error';
}

export function TestSuiteView(): JSX.Element {
  const C = useC();
  const api = useAdminApi();

  const [state, setState] = useState<ViewState>({ kind: 'loading' });
  const [refreshing, setRefreshing] = useState(false);
  const [escalate, setEscalate] = useState<EscalateContext | null>(null);

  const fetchRuns = useCallback(
    async (firstLoad: boolean): Promise<void> => {
      setRefreshing(true);
      if (firstLoad) setState({ kind: 'loading' });
      try {
        const payload = await api.getCiRuns();
        setState({ kind: 'ready', payload });
      } catch (err) {
        if (err instanceof ApiError && err.kind === 'unauthorized') {
          // Already routed to login by the http-client hook.
          return;
        }
        setState({ kind: 'error', message: errorMessage(err) });
      } finally {
        setRefreshing(false);
      }
    },
    [api],
  );

  useEffect(() => {
    void fetchRuns(true);
  }, [fetchRuns]);

  const handleRefresh = useCallback((): void => {
    void fetchRuns(false);
  }, [fetchRuns]);

  const handleEscalate = useCallback((run: AdminCiRun): void => {
    setEscalate({
      kind: 'ci_run',
      ref: String(run.id),
      summary: run.workflow_name,
      status: run.conclusion ?? run.status,
      detail: run.display_title,
    });
  }, []);

  const handleEscalateClose = useCallback((): void => {
    setEscalate(null);
  }, []);

  const payload = state.kind === 'ready' ? state.payload : null;
  const generatedAt = payload !== null ? payload.generated_at : null;
  const hasExport = payload !== null;

  return (
    <div className="max-w-7xl mx-auto px-3 sm:px-8 py-6 sm:py-10">
      {/* Kicker */}
      <div
        style={{
          fontFamily: monoFont,
          fontSize: 10,
          color: C.muted,
          letterSpacing: 2,
          textTransform: 'uppercase',
          marginBottom: 10,
        }}
      >
        QA console
      </div>

      <div className="flex flex-wrap items-end gap-3 sm:gap-4 mb-6 sm:mb-8">
        <div className="min-w-0">
          <h1
            style={{
              fontFamily: serifFont,
              fontSize: 'clamp(28px, 6vw, 44px)',
              lineHeight: 1,
              color: C.text,
              letterSpacing: -1.5,
              margin: 0,
            }}
          >
            Test Suite
          </h1>
          <div
            style={{
              fontFamily: monoFont,
              fontSize: 11,
              color: C.muted,
              letterSpacing: 0.5,
              marginTop: 10,
              lineHeight: 1.4,
              wordBreak: 'break-word',
            }}
          >
            {generatedAt !== null ? (
              <>Last fetched {formatIsoCompact(generatedAt)}</>
            ) : state.kind === 'error' ? (
              <>Last fetch failed</>
            ) : (
              <>Fetching CI runs…</>
            )}
          </div>
          <div
            style={{
              fontFamily: sansFont,
              fontSize: 13,
              color: C.body,
              marginTop: 10,
              lineHeight: 1.5,
              maxWidth: 640,
            }}
          >
            Each workflow's most recent run on{' '}
            <span style={{ fontFamily: monoFont, fontSize: 12 }}>main</span>. Failing
            workflows sort to the top. Escalate any failure into a GitHub issue
            without leaving the console.
          </div>
        </div>
      </div>

      {/* Controls row */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4 mb-6 sm:mb-8">
        <ConfigBadge payload={payload} />
        <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 w-full sm:w-auto">
          <ExportMenu
            disabled={!hasExport}
            onExportMarkdown={() => {
              if (payload !== null) downloadCiMarkdown(payload);
            }}
            onExportJson={() => {
              if (payload !== null) downloadCiJson(payload);
            }}
          />
          <RefreshButton running={refreshing} onClick={handleRefresh} />
        </div>
      </div>

      {/* Body */}
      {state.kind === 'loading' ? (
        <LoadingBoard />
      ) : state.kind === 'error' ? (
        <ErrorBoard message={state.message} />
      ) : state.payload.configured === false ? (
        <NotConfiguredBoard />
      ) : (
        <RunsBoard payload={state.payload} onEscalate={handleEscalate} />
      )}

      {/* Escalate dialog */}
      {escalate !== null ? (
        <EscalateDialog
          open={escalate !== null}
          context={escalate}
          onClose={handleEscalateClose}
        />
      ) : null}
    </div>
  );
}

/**
 * Small badge that shows whether the GitHub token is wired + an
 * inline error class if the most recent fetch failed upstream. Mirrors
 * the SummaryCounts position in the Health view.
 */
function ConfigBadge({ payload }: { payload: AdminCiRunsResponse | null }): JSX.Element {
  const C = useC();
  if (payload === null) {
    return (
      <div
        style={{
          fontFamily: monoFont,
          fontSize: 11,
          color: C.dim,
          letterSpacing: 0.8,
        }}
      >
        Loading…
      </div>
    );
  }
  if (payload.configured === false) {
    return (
      <div className="inline-flex items-center gap-2">
        <Pill tone="neutral">not connected</Pill>
        <span
          style={{
            fontFamily: monoFont,
            fontSize: 11,
            color: C.muted,
            letterSpacing: 0.5,
          }}
        >
          GitHub token required
        </span>
      </div>
    );
  }
  if (payload.error !== undefined && payload.error.length > 0) {
    return (
      <div className="inline-flex items-center gap-2 flex-wrap">
        <Pill tone="amber">github error</Pill>
        <span
          style={{
            fontFamily: monoFont,
            fontSize: 11,
            color: C.amberFg,
            letterSpacing: 0.5,
            wordBreak: 'break-word',
          }}
        >
          {payload.error}
        </span>
      </div>
    );
  }
  return (
    <div className="inline-flex items-center gap-2">
      <Pill tone="mint">connected</Pill>
      <span
        style={{
          fontFamily: monoFont,
          fontSize: 11,
          color: C.muted,
          letterSpacing: 0.5,
        }}
      >
        {payload.runs.length} {payload.runs.length === 1 ? 'workflow' : 'workflows'}
      </span>
    </div>
  );
}

function RefreshButton({
  running,
  onClick,
}: {
  running: boolean;
  onClick: () => void;
}): JSX.Element {
  const C = useC();
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={running}
      aria-label={running ? 'Refreshing CI runs' : 'Refresh CI runs'}
      aria-busy={running}
      className="w-full sm:w-auto inline-flex items-center justify-center gap-2"
      style={{
        background: running ? C.surface2 : C.lime,
        color: running ? C.muted : C.ink,
        border: `1px solid ${running ? C.border : C.lime}`,
        borderRadius: 2,
        padding: '10px 16px',
        minHeight: 40,
        fontFamily: monoFont,
        fontSize: 12,
        letterSpacing: 1.2,
        fontWeight: 500,
        textTransform: 'uppercase',
        cursor: running ? 'not-allowed' : 'pointer',
        opacity: running ? 0.8 : 1,
      }}
    >
      <RefreshCw size={14} aria-hidden="true" />
      {running ? 'Refreshing…' : 'Refresh'}
    </button>
  );
}

function LoadingBoard(): JSX.Element {
  const C = useC();
  return (
    <div className="flex flex-col gap-3">
      {Array.from({ length: 5 }, (_, i) => (
        <Card key={`ci-skel-${i}`} style={{ padding: 16 }}>
          <div
            style={{
              fontFamily: monoFont,
              fontSize: 12,
              color: C.dim,
              letterSpacing: 1,
            }}
          >
            Loading…
          </div>
        </Card>
      ))}
    </div>
  );
}

function ErrorBoard({ message }: { message: string }): JSX.Element {
  const C = useC();
  return (
    <Card style={{ padding: 18 }}>
      <div
        style={{
          fontFamily: monoFont,
          fontSize: 10,
          color: C.muted,
          letterSpacing: 2,
          textTransform: 'uppercase',
          marginBottom: 8,
        }}
      >
        CI runs endpoint unavailable
      </div>
      <div
        style={{
          fontFamily: monoFont,
          fontSize: 13,
          color: C.coralFg,
          lineHeight: 1.5,
          wordBreak: 'break-word',
        }}
      >
        {message}
      </div>
      <div
        style={{
          fontFamily: sansFont,
          fontSize: 12,
          color: C.muted,
          marginTop: 8,
          lineHeight: 1.5,
        }}
      >
        Re-running may recover; if it persists the API host is the problem
        (the route is fail-soft per fetch, so a hard error here means it
        didn't return).
      </div>
    </Card>
  );
}

function NotConfiguredBoard(): JSX.Element {
  const C = useC();
  return (
    <Card style={{ padding: 24 }}>
      <div className="flex items-start gap-3">
        <div
          style={{
            background: C.surface2,
            border: `1px solid ${C.borderSoft}`,
            borderRadius: 3,
            padding: 10,
            color: C.muted,
          }}
        >
          <FlaskConical size={18} aria-hidden="true" />
        </div>
        <div className="min-w-0">
          <div
            style={{
              fontFamily: monoFont,
              fontSize: 10,
              color: C.muted,
              letterSpacing: 2,
              textTransform: 'uppercase',
              marginBottom: 6,
            }}
          >
            Not connected
          </div>
          <h2
            style={{
              fontFamily: sansFont,
              fontSize: 16,
              fontWeight: 500,
              color: C.text,
              marginBottom: 8,
            }}
          >
            Connect GitHub to view CI runs
          </h2>
          <p
            style={{
              fontFamily: sansFont,
              fontSize: 13,
              color: C.body,
              lineHeight: 1.5,
              margin: 0,
              maxWidth: 560,
            }}
          >
            A read-only GitHub token must be set on the API (founder or ops
            action) before this board can fetch recent Actions runs. The
            board renders the moment the token is in place — no deploy
            required.
          </p>
        </div>
      </div>
    </Card>
  );
}

function RunsBoard({
  payload,
  onEscalate,
}: {
  payload: AdminCiRunsResponse;
  onEscalate: (run: AdminCiRun) => void;
}): JSX.Element {
  const C = useC();
  if (payload.runs.length === 0) {
    return (
      <Card style={{ padding: 18 }}>
        <div
          style={{
            fontFamily: monoFont,
            fontSize: 12,
            color: C.muted,
            letterSpacing: 0.5,
          }}
        >
          No runs to show.
        </div>
        {payload.error !== undefined && payload.error.length > 0 ? (
          <div
            style={{
              fontFamily: monoFont,
              fontSize: 12,
              color: C.amberFg,
              marginTop: 6,
            }}
          >
            GitHub fetch error: {payload.error}
          </div>
        ) : null}
      </Card>
    );
  }
  const sorted = rankCiRuns(payload.runs);
  return (
    <div className="flex flex-col gap-3">
      {sorted.map((r) => (
        <RunRow key={r.id} run={r} onEscalate={onEscalate} />
      ))}
    </div>
  );
}

function RunRow({
  run,
  onEscalate,
}: {
  run: AdminCiRun;
  onEscalate: (run: AdminCiRun) => void;
}): JSX.Element {
  const C = useC();
  const tone = conclusionToTone(run.conclusion, run.status);
  const conclusionLabel = run.conclusion ?? run.status.replace('_', ' ');
  const isFailing = run.conclusion === 'failure';

  // Collapsed by default — the row's facts are the answer to "what
  // ran"; the explanation answers "what do these fields mean" and
  // most viewings won't need it. Matches the HealthView pattern.
  const [expanded, setExpanded] = useState(false);
  const panelId = `ci-run-explain-${run.id}`;
  const toggle = useCallback((): void => {
    setExpanded((prev) => !prev);
  }, []);

  return (
    <Card style={{ padding: 16 }}>
      {/* Top row: pill + workflow + actions */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
        <div className="flex items-center gap-3 min-w-0">
          <Pill tone={tone}>{conclusionLabel}</Pill>
          <div
            style={{
              fontFamily: sansFont,
              fontSize: 14,
              fontWeight: 500,
              color: C.text,
              lineHeight: 1.3,
              wordBreak: 'break-word',
            }}
          >
            {run.workflow_name}
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <span
            style={{
              fontFamily: monoFont,
              fontSize: 10,
              color: C.muted,
              letterSpacing: 1,
              textTransform: 'uppercase',
            }}
          >
            {run.event}
          </span>
          {isFailing ? (
            <button
              type="button"
              onClick={() => onEscalate(run)}
              style={{
                fontFamily: monoFont,
                fontSize: 10,
                color: C.coralFg,
                background: C.coralDim,
                border: '1px solid rgba(255,107,71,0.3)',
                borderRadius: 2,
                padding: '6px 10px',
                minHeight: 32,
                letterSpacing: 1,
                fontWeight: 500,
                textTransform: 'uppercase',
                cursor: 'pointer',
              }}
            >
              Escalate
            </button>
          ) : null}
        </div>
      </div>

      {/* Display title */}
      {run.display_title.length > 0 ? (
        <div
          style={{
            fontFamily: sansFont,
            fontSize: 13,
            color: C.body,
            marginTop: 10,
            lineHeight: 1.4,
            wordBreak: 'break-word',
          }}
        >
          {run.display_title}
        </div>
      ) : null}

      {/* Commit message */}
      {run.head_commit_message.length > 0 ? (
        <div
          style={{
            fontFamily: monoFont,
            fontSize: 12,
            color: C.muted,
            marginTop: 6,
            lineHeight: 1.4,
            wordBreak: 'break-word',
          }}
        >
          {run.head_commit_message}
        </div>
      ) : null}

      {/* Branch + sha + time + link */}
      <div
        className="flex items-center justify-between gap-2 flex-wrap"
        style={{ marginTop: 10 }}
      >
        <div className="flex items-center gap-2 flex-wrap">
          <span
            style={{
              fontFamily: monoFont,
              fontSize: 11,
              color: C.muted,
              letterSpacing: 0.5,
            }}
          >
            {run.branch}
          </span>
          <span
            style={{
              fontFamily: monoFont,
              fontSize: 11,
              color: C.dim,
            }}
          >
            ·
          </span>
          <span
            style={{
              fontFamily: monoFont,
              fontSize: 11,
              color: C.body,
              background: C.surface2,
              border: `1px solid ${C.borderSoft}`,
              borderRadius: 2,
              padding: '1px 6px',
            }}
          >
            {run.head_sha}
          </span>
          <span
            style={{
              fontFamily: monoFont,
              fontSize: 11,
              color: C.dim,
            }}
          >
            ·
          </span>
          <span
            style={{
              fontFamily: monoFont,
              fontSize: 11,
              color: C.muted,
              letterSpacing: 0.5,
            }}
            title={`${formatIsoCompact(run.updated_at)}`}
          >
            {formatRelativeShort(run.updated_at)}{' '}
            <span style={{ color: C.dim }}>
              ({formatIsoCompact(run.updated_at)})
            </span>
          </span>
        </div>
        <a
          href={run.html_url}
          target="_blank"
          rel="noreferrer noopener"
          className="inline-flex items-center gap-1"
          style={{
            fontFamily: monoFont,
            fontSize: 10,
            color: C.muted,
            letterSpacing: 1.2,
            textTransform: 'uppercase',
            textDecoration: 'none',
            padding: '6px 0',
            minHeight: 32,
          }}
        >
          View on GitHub
          <ExternalLink size={12} aria-hidden="true" />
        </a>
      </div>

      {/* "What is this?" toggle row — small mono kicker on the left,
          stable run id on the right (mirrors HealthView's CheckRow). */}
      <div
        className="flex items-center justify-between gap-2 flex-wrap"
        style={{ marginTop: 10 }}
      >
        <button
          type="button"
          onClick={toggle}
          aria-expanded={expanded}
          aria-controls={panelId}
          className="inline-flex items-center gap-1.5"
          style={{
            background: 'transparent',
            border: 'none',
            padding: '8px 0',
            minHeight: 40,
            color: C.muted,
            fontFamily: monoFont,
            fontSize: 10,
            letterSpacing: 1.5,
            textTransform: 'uppercase',
            cursor: 'pointer',
            outline: 'none',
          }}
        >
          <span>{expanded ? 'Hide' : 'What is this?'}</span>
          <ChevronDown
            size={12}
            aria-hidden="true"
            style={{
              transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
              transition: 'transform 120ms ease',
            }}
          />
        </button>
        <span
          style={{
            fontFamily: monoFont,
            fontSize: 9,
            color: C.dim,
            letterSpacing: 1,
            textTransform: 'uppercase',
          }}
        >
          run #{run.id}
        </span>
      </div>

      {/* Explanation panel — collapsed by default. Static plain-language
          legend for the fields on THIS row; not per-run-dynamic. `hidden`
          keeps the content out of the a11y tree when collapsed (matches
          aria-expanded=false on the toggle). */}
      <div
        id={panelId}
        hidden={!expanded}
        style={{
          marginTop: expanded ? 10 : 0,
          paddingTop: expanded ? 10 : 0,
          borderTop: expanded ? `1px solid ${C.borderSoft}` : 'none',
        }}
      >
        <div
          style={{
            fontFamily: monoFont,
            fontSize: 9,
            color: C.muted,
            letterSpacing: 2,
            textTransform: 'uppercase',
            marginBottom: 6,
          }}
        >
          What these fields mean
        </div>
        <RunExplanation isFailing={isFailing} />
      </div>
    </Card>
  );
}

/**
 * Static plain-language legend for the fields on a CI-run row. The
 * content is the SAME on every row — the live run data is the value
 * above; this is the durable meaning. Kept here so the row carries
 * its own help without a separate doc round-trip.
 */
function RunExplanation({ isFailing }: { isFailing: boolean }): JSX.Element {
  const C = useC();
  return (
    <>
      <p
        style={{
          fontFamily: sansFont,
          fontSize: 13,
          color: C.body,
          lineHeight: 1.55,
          margin: '0 0 8px 0',
        }}
      >
        Each row is one workflow's most recent run on{' '}
        <span style={{ fontFamily: monoFont, fontSize: 12 }}>main</span>. The
        field meanings below are durable — the values above are this run.
      </p>
      <ul
        style={{
          fontFamily: sansFont,
          fontSize: 13,
          color: C.body,
          lineHeight: 1.55,
          margin: 0,
          paddingLeft: 18,
        }}
      >
      <li style={{ marginBottom: 4 }}>
        <strong style={{ color: C.text, fontWeight: 500 }}>Conclusion</strong>{' '}
        (the coloured pill): the run's outcome —{' '}
        <span style={{ fontFamily: monoFont, fontSize: 12 }}>success</span> /{' '}
        <span style={{ fontFamily: monoFont, fontSize: 12 }}>failure</span> /{' '}
        <span style={{ fontFamily: monoFont, fontSize: 12 }}>cancelled</span> /{' '}
        <span style={{ fontFamily: monoFont, fontSize: 12 }}>in-progress</span> /{' '}
        <span style={{ fontFamily: monoFont, fontSize: 12 }}>queued</span>.
        Failure means at least one job in the run failed.
      </li>
      <li style={{ marginBottom: 4 }}>
        <strong style={{ color: C.text, fontWeight: 500 }}>Workflow</strong>:
        which GitHub Actions workflow ran (e.g. ingest-heartbeat, AI Review).
      </li>
      <li style={{ marginBottom: 4 }}>
        <strong style={{ color: C.text, fontWeight: 500 }}>Event</strong>: what
        triggered it —{' '}
        <span style={{ fontFamily: monoFont, fontSize: 12 }}>schedule</span> (a
        cron timer),{' '}
        <span style={{ fontFamily: monoFont, fontSize: 12 }}>push</span>,{' '}
        <span style={{ fontFamily: monoFont, fontSize: 12 }}>pull_request</span>,
        or{' '}
        <span style={{ fontFamily: monoFont, fontSize: 12 }}>
          workflow_dispatch
        </span>{' '}
        (manual).
      </li>
      <li style={{ marginBottom: 4 }}>
        <strong style={{ color: C.text, fontWeight: 500 }}>Branch / commit</strong>:
        the branch and short commit SHA the run executed against; the line under
        it is that commit's message.
      </li>
      <li style={{ marginBottom: 4 }}>
        <strong style={{ color: C.text, fontWeight: 500 }}>Time</strong>: when
        the run last updated (ET).
      </li>
      <li style={{ marginBottom: 4 }}>
        <strong style={{ color: C.text, fontWeight: 500 }}>View on GitHub</strong>:
        opens this run's page on GitHub for the full logs.
      </li>
      {isFailing ? (
        <li>
          <strong style={{ color: C.text, fontWeight: 500 }}>Escalate</strong>:
          files a private GitHub issue about this failure so it can be tracked.
        </li>
      ) : null}
      </ul>
    </>
  );
}

function conclusionToTone(
  conclusion: AdminCiRun['conclusion'],
  status: AdminCiRun['status'],
): PillTone {
  if (conclusion === 'success') return 'mint';
  if (conclusion === 'failure') return 'coral';
  if (conclusion === 'cancelled') return 'amber';
  if (status === 'in_progress') return 'sky';
  if (status === 'queued') return 'neutral';
  return 'neutral';
}
